import animations from './animations.js';
import modMover from './modMover.js';
import checkCmsPaths from './checkCmsPaths.js';
import {manageBehaviors} from '@area17/a17-behaviors';
import * as Behaviors from './behaviors';
import DOMReady from './helpers/DOMReady.js';
import * as csrf from './helpers/csrf.js';
import cssConfig from '@css/app.config.json';
import * as Sentry from "@sentry/browser";

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log('HMR');
    });
}

checkCmsPaths.init();

DOMReady(async () => {
    animations.init();

    window.CO = window.CO || {};
    window.CO.modMover = modMover;
    window.CO.structure = cssConfig.structure;
    // Import the shared CSRF helper from the eagerly-loaded entry so it lands in
    // the app chunk (not a lazy behavior-only split chunk). Vite preloads the
    // lazy behaviors' transitive deps against the absolute base (/co/dist/),
    // which 404s wherever the app isn't served under /co/ (e.g. the CI test
    // server at /dist/); keeping it in the entry avoids that.
    window.CO.csrf = csrf;

    let breakpoints = [];
    if (cssConfig.structure && cssConfig.structure.breakpoints) {
        for (const [key, value] of Object.entries(cssConfig.structure.breakpoints)) {
            breakpoints.push({name: key, start: value});
        }
        breakpoints = breakpoints.sort(
            (a, b) => parseInt(a.start) - parseInt(b.start),
        );
        breakpoints = breakpoints.map((a) => a.name);
    }

    // Prepare breakpoints for @area17/a17-behaviors package
    window.A17 = window.A17 || {};
    window.A17.behaviors = manageBehaviors;
    window.A17.behaviors.init(Behaviors, {
        breakpoints: breakpoints,
        dynamicBehaviors: import.meta.glob(['@/behaviors-dynamic/*.js']),
    });

    // Select all focusable elements
    const focusableElements = Array.from(
        document.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    let currentIndex = -1;

    document.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {

            // Skip arrow navigation if user is editing in a text input
            const isTextInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName);
            if (isTextInput) {
                return; // Allow normal arrow key behavior in form fields
            }
            if (currentIndex === -1) {
                currentIndex = 0; // Set initial focus to the first element if not already focused
            } else {
                if (event.key === 'ArrowRight') {
                    currentIndex = (currentIndex + 1) % focusableElements.length;
                } else if (event.key === 'ArrowLeft') {
                    currentIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
                }
            }
            focusableElements[currentIndex].focus();
            event.preventDefault();
        }
    });

    document.body.classList.add('loaded');
    // fire custom event to let other scripts know app is loaded
    document.dispatchEvent(new Event('app:loaded'));

    Sentry.init({
        dsn: "https://918e341f3f2927d38bfab0979bd7cd42@o4507211308007424.ingest.us.sentry.io/4507211310497792",
        defaultIntegrations: false,
        sendDefaultPii: true,
        integrations: [
            Sentry.feedbackIntegration({
                // Disable the injection of the default widget
                autoInject: false,
                isNameRequired: true,
                isEmailRequired: true,
                triggerLabel: "Question or Problem?",
                formTitle: "How can we help?",
                submitButtonLabel: "Submit Feedback",
                messagePlaceholder: "Describe what you need help with. If something went wrong, describe what happened in detail.",
                showBranding: false,
            }),
        ]
    });

    const currentUser = window.currentUser || {};
    const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ').trim();
    const sentryUser = {};

    if (currentUser.id || currentUser.uuid) {
        sentryUser.id = currentUser.id || currentUser.uuid;
    }

    if (currentUser.loginEmail || currentUser.email) {
        sentryUser.email = currentUser.loginEmail || currentUser.email;
    }

    if (fullName) {
        sentryUser.username = fullName;
    }

    if (Object.keys(sentryUser).length > 0) {
        Sentry.setUser(sentryUser);
    }
});
